import { Container, Typography, Box, List, ListItem, ListItemText } from "@mui/material";
// @ts-ignore
import AppLogo from "../ui-components/app-logo";
import { AppConst } from "../common/app-constants.ts";

export default function PrivacyPolicyPage() {
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <AppLogo />
      <Typography variant="h4" gutterBottom>
        Privacy Policy of {AppConst.APP_NAME}
      </Typography>
      <Typography paragraph>
        This Application collects some Personal Data from its Users.
      </Typography>
      <Typography paragraph>
        This document can be printed for reference by using the print command in
        the settings of any browser.
      </Typography>

      <SectionHeading>Owner and Data Controller</SectionHeading>
      <Typography paragraph>
        Substring LLC, 6800 Wisconsin Ave #1009, Chevy Chase, MD 20815. USA.
      </Typography>
      <Typography paragraph>Owner contact email: info@substring.org</Typography>

      <SectionHeading>Types of Data collected</SectionHeading>
      <Typography paragraph>
        Among the types of Personal Data that this Application collects, by
        itself or through third parties, there are: first name; last name; email
        address; Tracker; Usage Data; company name; website.
      </Typography>
      <Typography paragraph>
        Complete details on each type of Personal Data collected are provided in
        the dedicated sections of this privacy policy or by specific explanation
        texts displayed prior to the Data collection. Personal Data may be freely
        provided by the User, or, in case of Usage Data, collected automatically
        when using this Application. Unless specified otherwise, all Data
        requested by this Application is mandatory and failure to provide this
        Data may make it impossible for this Application to provide its services.
        In cases where this Application specifically states that some Data is not
        mandatory, Users are free not to communicate this Data without
        consequences to the availability or the functioning of the Service.
      </Typography>
      <Typography paragraph>
        Users who are uncertain about which Personal Data is mandatory are
        welcome to contact the Owner. Any use of Cookies — or of other tracking
        tools — by this Application or by the owners of third-party services used
        by this Application serves the purpose of providing the Service required
        by the User, in addition to any other purposes described in the present
        document and in the Cookie Policy, if available.
      </Typography>
      <Typography paragraph>
        Users are responsible for any third-party Personal Data obtained,
        published or shared through this Application and confirm that they have
        the third party's consent to provide the Data to the Owner.
      </Typography>

      <SectionHeading>Mode and place of processing the Data</SectionHeading>

      <Typography variant="h6" gutterBottom>
        Methods of processing
      </Typography>
      <Typography paragraph>
        The Owner takes appropriate security measures to prevent unauthorized
        access, disclosure, modification, or unauthorized destruction of the
        Data. The Data processing is carried out using computers and/or IT
        enabled tools, following organizational procedures and modes strictly
        related to the purposes indicated. In addition to the Owner, in some
        cases, the Data may be accessible to certain types of persons in charge,
        involved with the operation of this Application (administration, sales,
        marketing, legal, system administration) or external parties (such as
        third-party technical service providers, mail carriers, hosting
        providers, IT companies, communications agencies) appointed, if
        necessary, as Data Processors by the Owner. The updated list of these
        parties may be requested from the Owner at any time.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Legal basis of processing
      </Typography>
      <Typography paragraph>
        The Owner may process Personal Data relating to Users if one of the
        following applies:
      </Typography>
      <BulletList
        items={[
          "Users have given their consent for one or more specific purposes. Note: Under some legislations the Owner may be allowed to process Personal Data until the User objects to such processing (\"opt-out\"), without having to rely on consent or any other of the following legal bases. This, however, does not apply, whenever the processing of Personal Data is subject to European data protection law;",
          "provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;",
          "processing is necessary for compliance with a legal obligation to which the Owner is subject;",
          "processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;",
          "processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.",
        ]}
      />
      <Typography paragraph>
        In any case, the Owner will gladly help to clarify the specific legal
        basis that applies to the processing, and in particular whether the
        provision of Personal Data is a statutory or contractual requirement, or
        a requirement necessary to enter into a contract.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Place
      </Typography>
      <Typography paragraph>
        The Data is processed at the Owner's operating offices and in any other
        places where the parties involved in the processing are located.
        Depending on the User's location, data transfers may involve transferring
        the User's Data to a country other than their own. To find out more
        about the place of processing of such transferred Data, Users can check
        the section containing details about the processing of Personal Data.
      </Typography>
      <Typography paragraph>
        Users are also entitled to learn about the legal basis of Data transfers
        to a country outside the European Union or to any international
        organization governed by public international law or set up by two or
        more countries, such as the UN, and about the security measures taken by
        the Owner to safeguard their Data. If any such transfer takes place,
        Users can find out more by checking the relevant sections of this
        document or inquire with the Owner using the information provided in the
        contact section.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Retention time
      </Typography>
      <Typography paragraph>
        Personal Data shall be processed and stored for as long as required by
        the purpose they have been collected for.
      </Typography>
      <Typography paragraph>Therefore:</Typography>
      <BulletList
        items={[
          "Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.",
          "Personal Data collected for the purposes of the Owner's legitimate interests shall be retained as long as needed to fulfill such purposes. Users may find specific information regarding the legitimate interests pursued by the Owner within the relevant sections of this document or by contacting the Owner.",
          "The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn. Furthermore, the Owner may be obliged to retain Personal Data for a longer period whenever required to do so for the performance of a legal obligation or upon order of an authority.",
        ]}
      />
      <Typography paragraph>
        Once the retention period expires, Personal Data shall be deleted.
        Therefore, the right of access, the right to erasure, the right to
        rectification and the right to data portability cannot be enforced after
        expiration of the retention period.
      </Typography>

      <SectionHeading>The purposes of processing</SectionHeading>
      <Typography paragraph>
        The Data concerning the User is collected to allow the Owner to provide
        its Service, comply with its legal obligations, respond to enforcement
        requests, protect its rights and interests (or those of its Users or
        third parties), detect any malicious or fraudulent activity, as well as
        the following: Access to third-party accounts, Analytics, Collection of
        privacy-related preferences and Contacting the User.
      </Typography>
      <Typography paragraph>
        For specific information about the Personal Data used for each purpose,
        the User may refer to the section "Detailed information on the processing
        of Personal Data".
      </Typography>

      <SectionHeading>
        Detailed information on the processing of Personal Data
      </SectionHeading>
      <Typography paragraph>
        Personal Data is collected for the following purposes and using the
        following services:
      </Typography>

      <Typography variant="h6" gutterBottom>
        Access to third-party accounts
      </Typography>
      <Typography paragraph>
        This type of service allows this Application to access Data from your
        account on a third-party service and perform actions with it. These
        services are not activated automatically, but require explicit
        authorization by the User.
      </Typography>
      <Typography paragraph>
        <strong>Stripe account access (Stripe Inc)</strong> — This service allows
        this Application to connect with the User's account on Stripe, provided
        by Stripe, Inc. Personal Data processed: email address; first name; last
        name. Place of processing: United States.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Analytics
      </Typography>
      <Typography paragraph>
        The services contained in this section enable the Owner to monitor and
        analyze web traffic and can be used to keep track of User behavior.
      </Typography>
      <Typography paragraph>
        <strong>Google Analytics (Google LLC)</strong> — Google Analytics is a web
        analysis service provided by Google LLC ("Google"). Google utilizes the
        Data collected to track and examine the use of this Application, to
        prepare reports on its activities and share them with other Google
        services. Google may use the Data collected to contextualize and
        personalize the ads of its own advertising network. Personal Data
        processed: Tracker; Usage Data. Place of processing: United States.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Collection of privacy-related preferences
      </Typography>
      <Typography paragraph>
        This Application uses cookies and similar tracking technologies to
        collect and store Users' preferences related to the processing of
        personal information. Users can control and manage their cookie
        preferences through their browser settings.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Contacting the User
      </Typography>
      <Typography paragraph>
        <strong>Contact form (this Application)</strong> — By filling in the
        contact form with their Data, the User authorizes this Application to use
        these details to reply to requests for information, quotes or any other
        kind of request as indicated by the form's header. Personal Data
        processed: company name; email address; first name; last name; website.
      </Typography>

      <Typography variant="h6" gutterBottom>
        Payment Processing
      </Typography>
      <Typography paragraph>
        This Application processes payments through Stripe, a third-party payment
        processor. When making payments, Users' payment information is processed
        directly by Stripe and is subject to Stripe's privacy policy. The
        Application does not store or process Users' payment card information
        directly. Personal Data processed for payment purposes: email address;
        first name; last name; payment information (processed by Stripe).
      </Typography>

      <SectionHeading>The rights of Users</SectionHeading>
      <Typography paragraph>
        Users may exercise certain rights regarding their Data processed by the
        Owner. In particular, Users have the right to do the following:
      </Typography>
      <BulletList
        items={[
          "Withdraw their consent at any time.",
          "Object to processing of their Data.",
          "Access their Data.",
          "Verify and seek rectification.",
          "Restrict the processing of their Data.",
          "Have their Personal Data deleted or otherwise removed.",
          "Receive their Data and have it transferred to another controller.",
          "Lodge a complaint.",
        ]}
      />

      <Typography variant="h6" gutterBottom>
        Details about the right to object to processing
      </Typography>
      <Typography paragraph>
        Where Personal Data is processed for a public interest, in the exercise
        of an official authority vested in the Owner or for the purposes of the
        legitimate interests pursued by the Owner, Users may object to such
        processing by providing a ground related to their particular situation to
        justify the objection.
      </Typography>
      <Typography paragraph>
        Users must know that, however, should their Personal Data be processed
        for direct marketing purposes, they can object to that processing at any
        time without providing any justification.
      </Typography>

      <Typography variant="h6" gutterBottom>
        How to exercise these rights
      </Typography>
      <Typography paragraph>
        Any requests to exercise User rights can be directed to the Owner through
        the contact details provided in this document. These requests can be
        exercised free of charge and will be addressed by the Owner as early as
        possible and always within one month.
      </Typography>

      <SectionHeading>
        Additional information about Data collection and processing
      </SectionHeading>
      <Typography paragraph>
        <strong>Legal action</strong> — The User's Personal Data may be used for
        legal purposes by the Owner in Court or in the stages leading to possible
        legal action arising from improper use of this Application or the related
        Services. The User declares to be aware that the Owner may be required to
        reveal personal data upon request of public authorities.
      </Typography>
      <Typography paragraph>
        <strong>System logs and maintenance</strong> — For operation and
        maintenance purposes, this Application and any third-party services may
        collect files that record interaction with this Application (System logs)
        use other Personal Data (such as the IP Address) for this purpose.
      </Typography>
      <Typography paragraph>
        <strong>How "Do Not Track" requests are handled</strong> — This
        Application does not support "Do Not Track" requests. To determine
        whether any of the third-party services it uses honor the "Do Not Track"
        requests, please read their privacy policies.
      </Typography>
      <Typography paragraph>
        <strong>Changes to this privacy policy</strong> — The Owner reserves the
        right to make changes to this privacy policy at any time by notifying its
        Users on this page and possibly within this Application. It is strongly
        recommended to check this page often, referring to the date of the last
        modification listed at the bottom.
      </Typography>

      <SectionHeading>Definitions and legal references</SectionHeading>
      <Typography paragraph>
        <strong>Personal Data (or Data)</strong> — Any information that directly,
        indirectly, or in connection with other information — including a personal
        identification number — allows for the identification or identifiability
        of a natural person.
      </Typography>
      <Typography paragraph>
        <strong>Usage Data</strong> — Information collected automatically through
        this Application (or third-party services employed in this Application),
        which can include: the IP addresses or domain names of the computers
        utilized by the Users who use this Application, the URI addresses, the
        time of the request, the method utilized to submit the request to the
        server, the size of the file received in response, the numerical code
        indicating the status of the server's answer, the country of origin, the
        features of the browser and the operating system utilized by the User,
        the various time details per visit and the details about the path
        followed within the Application with special reference to the sequence of
        pages visited, and other parameters about the device operating system
        and/or the User's IT environment.
      </Typography>
      <Typography paragraph>
        <strong>User</strong> — The individual using this Application who, unless
        otherwise specified, coincides with the Data Subject.
      </Typography>
      <Typography paragraph>
        <strong>Data Subject</strong> — The natural person to whom the Personal
        Data refers.
      </Typography>
      <Typography paragraph>
        <strong>Data Processor (or Data Supervisor)</strong> — The natural or
        legal person, public authority, agency or other body which processes
        Personal Data on behalf of the Controller, as described in this privacy
        policy.
      </Typography>
      <Typography paragraph>
        <strong>Data Controller (or Owner)</strong> — The natural or legal person,
        public authority, agency or other body which, alone or jointly with
        others, determines the purposes and means of the processing of Personal
        Data, including the security measures concerning the operation and use of
        this Application. The Data Controller, unless otherwise specified, is the
        Owner of this Application.
      </Typography>
      <Typography paragraph>
        <strong>This Application</strong> — The means by which the Personal Data
        of the User is collected and processed.
      </Typography>
      <Typography paragraph>
        <strong>Service</strong> — The service provided by this Application as
        described in the relative terms (if available) and on this
        site/application.
      </Typography>
      <Typography paragraph>
        <strong>European Union (or EU)</strong> — Unless otherwise specified, all
        references made within this document to the European Union include all
        current member states to the European Union and the European Economic
        Area.
      </Typography>
      <Typography paragraph>
        <strong>Cookie</strong> — Cookies are Trackers consisting of small sets
        of data stored in the User's browser.
      </Typography>
      <Typography paragraph>
        <strong>Tracker</strong> — Tracker indicates any technology — e.g
        Cookies, unique identifiers, web beacons, embedded scripts, e-tags and
        fingerprinting — that enables the tracking of Users, for example by
        accessing or storing information on the User's device.
      </Typography>

      <Box sx={{ mt: 2, mb: 4 }}>
        <Typography variant="body2" color="text.secondary">
          This privacy statement has been prepared based on provisions of
          multiple legislations, including Art. 13/14 of Regulation (EU)
          2016/679 (General Data Protection Regulation). This privacy policy
          relates solely to this Application, if not stated otherwise within this
          document.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Latest update: May 8, 2025
        </Typography>
      </Box>
    </Container>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <List dense disablePadding sx={{ pl: 2, mb: 1 }}>
      {items.map((item, i) => (
        <ListItem key={i} sx={{ display: "list-item", listStyleType: "disc", pl: 0 }}>
          <ListItemText primary={item} />
        </ListItem>
      ))}
    </List>
  );
}
